/**
 * PaymentPageLayout - Responsive layout for Payment Page V2
 *
 * Desktop: Two columns (left: content, right: sticky payment card)
 * Mobile: Single column with specific ordering per Figma design
 *
 * CRITICAL: Payment card with Stripe Elements must only be rendered ONCE.
 * This layout uses CSS Grid with named areas to position the single
 * payment card instance in different visual positions per breakpoint.
 */

import { ReactNode } from 'react';
import '../dashboard/dashboard-reset.css';
import './PaymentPageLayout.css';

interface PaymentPageLayoutProps {
  /** Hero section (trial badge + headline). Optional — non-trial layouts embed the headline in paymentCard. */
  hero?: ReactNode;
  /** Payment card content */
  paymentCard: ReactNode;
  /** Features list ("What's included"). Optional — funnel Step 1 now covers this. */
  featuresList?: ReactNode;
  /** Testimonials section */
  testimonials: ReactNode;
  /** Privacy section */
  privacy: ReactNode;
  /** FAQ section */
  faqs: ReactNode;
  /** Contact button */
  contactButton: ReactNode;
  /** Header content */
  header?: ReactNode;
  /** Footer content */
  footer?: ReactNode;
  /** Sticky mobile CTA */
  stickyMobileCTA?: ReactNode;
}

export function PaymentPageLayout({
  hero,
  paymentCard,
  featuresList,
  testimonials,
  privacy,
  faqs,
  contactButton,
  header,
  footer,
  stickyMobileCTA,
}: PaymentPageLayoutProps) {
  return (
    <div className="dashboard-reset min-h-screen bg-catch-tan">
      {/* Header — full-bleed border, content stays centered */}
      {header && (
        <div className="w-full border-b border-solid border-[#E3DCCE]">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="relative">{header}</div>
          </div>
        </div>
      )}

      {/* Container - ~1024px max width for desktop two-column layout */}
      <div className="mx-auto max-w-5xl px-4 pb-24 sm:px-6 md:pb-8 lg:px-8">

        {/* Main Layout */}
        <div className="payment-layout pt-2 lg:py-8">
          {/* Left Column - content only. Two clean vertical channels on desktop:
              left = everything to read; right = everything to pay. */}
          <div className="payment-layout__left-column">
            {/* Hero */}
            {hero && <div className="payment-layout__hero">{hero}</div>}

            {/* Features */}
            {featuresList && (
              <div className="payment-layout__features">
                {featuresList}
              </div>
            )}

            {/* Testimonials */}
            <div className="payment-layout__testimonials">{testimonials}</div>

            {/* Privacy */}
            <div className="payment-layout__privacy">{privacy}</div>

            {/* FAQs + Contact - desktop renders here in the left column; on
                mobile they're handled by the grid-area below. */}
            <div className="hidden lg:block">
              {faqs}
              <div className="mt-8 flex justify-center">{contactButton}</div>
            </div>
          </div>

          {/* Payment Card (right column on desktop) - payment-only, sticky */}
          <div className="payment-layout__sidebar">
            <div className="lg:sticky lg:top-8">
              {paymentCard}
            </div>
          </div>

          {/* FAQs + Contact - mobile only, after testimonials */}
          <div className="payment-layout__faqs lg:hidden">
            {faqs}
            <div className="mt-8 pb-8 flex justify-center">{contactButton}</div>
          </div>
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-slate-100 py-8">{footer}</div>
        )}
      </div>

      {/* Sticky Mobile CTA - Fixed at bottom on mobile only */}
      {stickyMobileCTA}
    </div>
  );
}

export default PaymentPageLayout;
